import * as Component from "../component";
import * as Keyboard from "../keyboard";

document.addEventListener("omnisearch:mounted", (event) => {
  const omnisearchEl = event.target;

  const resultsEl = getDefaultResultsEl(omnisearchEl);

  init(omnisearchEl, {
    resultsEl,
  });
});

function getDefaultResultsEl(omnisearchEl) {
  const sibling = omnisearchEl.nextElementSibling;
  return sibling?.classList.contains("omnisearch__results") ? sibling : null;
}

/**
 * Initialise a search input.
 *
 * @param {HTMLElement} omnisearchEl - The wrapper label of the search input
 * @param {Object} opts
 * @param {HTMLElement | null} opts.resultsEl - The results element accompagnying the search control
 */
export function init(omnisearchEl, opts = {}) {
  const inputEl = omnisearchEl.querySelector("input");

  const { resultsEl } = opts;
  Component.setExtra(omnisearchEl, "resultsEl", resultsEl);

  initInput(omnisearchEl, inputEl);
  initButton(omnisearchEl, inputEl);
  initKeyboardNavigation(omnisearchEl, inputEl, opts);
  initHoverSuppression(omnisearchEl, resultsEl);
}

// While the user navigates with arrow keys, the mouse may still be hovering an
// unrelated result and would highlight it via `:hover`, competing with the
// keyboard-focused item. Add a class to the results container that disables
// hover styles until the mouse moves again.
function initHoverSuppression(omnisearchEl, resultsEl) {
  if (!resultsEl) return;
  const KEY_NAV_KEYS = new Set(["ArrowUp", "ArrowDown"]);
  omnisearchEl.addEventListener("keydown", (event) => {
    if (KEY_NAV_KEYS.has(event.key)) {
      resultsEl.classList.add("omnisearch__results--keyboard-nav");
    }
  });
  resultsEl.addEventListener("mousemove", () => {
    resultsEl.classList.remove("omnisearch__results--keyboard-nav");
  });
}

function initInput(omnisearchEl, inputEl) {
  inputEl.addEventListener("focus", () => open(omnisearchEl));
}

function initButton(omnisearchEl, inputEl) {
  const buttonEl = omnisearchEl.querySelector("button");

  buttonEl.addEventListener("click", (_event) => {
    if (Component.getExtra(omnisearchEl, "open")) {
      clearInput(inputEl);
      close(omnisearchEl);
    } else {
      inputEl.focus();
    }
  });
}

function initKeyboardNavigation(omnisearchEl, inputEl, opts) {
  const { resultsEl } = opts;

  Keyboard.initListNavigation({
    containerEl: resultsEl,
    triggerEl: inputEl,

    handle(event) {
      if (event.key === "Escape") {
        clearInput(inputEl);
        close(omnisearchEl);
        return true;
      }
    },
  });
}

export function clearInput(inputEl) {
  inputEl.blur();
  inputEl.value = "";
  inputEl.dispatchEvent(new Event("input"));
}

/**
 * Open the omnisearch component.
 *
 * @param {HTMLElement} omnisearchEl - The root element of the omnisearch component
 */
export function open(omnisearchEl) {
  Component.setExtra(omnisearchEl, "open", true);

  const resultsEl = Component.getExtra(omnisearchEl, "resultsEl");
  if (resultsEl) {
    toggleResultsEl(resultsEl, true);
    const closeOnOutsideClick = (event) => closeResultsOnOutsideClick(event, omnisearchEl);
    const closeOnFocusOutside = (event) => closeResultsOnFocusOutside(event, omnisearchEl);
    Component.setExtra(omnisearchEl, "closeOnOutsideClick", closeOnOutsideClick);
    Component.setExtra(omnisearchEl, "closeOnFocusOutside", closeOnFocusOutside);
    document.addEventListener("click", closeOnOutsideClick);
    // Listen on `focusin` (bubbles) so we can detect focus moving anywhere outside
    // the omnisearch label and its sibling results container — the previous
    // `focusout` on `omnisearchEl` did not fire when focus moved out of a result.
    document.addEventListener("focusin", closeOnFocusOutside);
  }
}

/**
 * Close the omnisearch component.
 *
 * @param {HTMLElement} omnisearchEl - The root element of the omnisearch component
 */
export function close(omnisearchEl) {
  Component.setExtra(omnisearchEl, "open", false);

  const resultsEl = Component.getExtra(omnisearchEl, "resultsEl");
  if (resultsEl) {
    toggleResultsEl(resultsEl, false);
    document.removeEventListener("click", Component.getExtra(omnisearchEl, "closeOnOutsideClick"));
    document.removeEventListener("focusin", Component.getExtra(omnisearchEl, "closeOnFocusOutside"));
  }
}

/**
 * Open or closes the omnisearch component.
 *
 * @param {HTMLElement} omnisearchEl - The root element of the omnisearch component
 * @returns {boolean} - Whether the omnisearch is now open or not
 */
export function toggle(omnisearchEl) {
  if (Component.getExtra(omnisearchEl, "open")) {
    close(omnisearchEl);
    return false;
  } else {
    open(omnisearchEl);
    return true;
  }
}

function closeResultsOnOutsideClick(event, omnisearchEl) {
  const resultsEl = Component.getExtra(omnisearchEl, "resultsEl");
  if (omnisearchEl.contains(event.target) || resultsEl.contains(event.target)) return;

  close(omnisearchEl);
}

function closeResultsOnFocusOutside(event, omnisearchEl) {
  const resultsEl = Component.getExtra(omnisearchEl, "resultsEl");
  if (omnisearchEl.contains(event.target) || resultsEl.contains(event.target)) return;

  close(omnisearchEl);
}

function toggleResultsEl(resultsEl, force) {
  resultsEl.classList.toggle("omnisearch__results--open", force);
}
